import React, { useState, useEffect } from "react"
import { withRouter, Redirect } from "react-router-dom"
import queryString from "query-string"
import SignUpStep1Container from "../../containers/SignUpContainer/SignUpStep1Container"
import SignUpCompnayStep2Container from "../../containers/SignUpContainer/SignUpCompnayStep2Container"
import SignUpIndividualStep2Container from "../../containers/SignUpContainer/SignUpIndividualStep2Container"
import SignUpIndividualStep3Container from "../../containers/SignUpContainer/SignUpIndividualStep3Container"
import { paths } from "../../routes/paths"
import services from "../../services"
import SignUpCompanyStep3Container from "../../containers/SignUpContainer/SignUpCompanyStep3Container"
import {
  bestDescribesOptions,
  whichBusinessTypeOptions,
} from "../../forms/SignUpApplyForms/SignUpApplyValuesHelper"

const SignUpPage = ({ history, match, location }) => {
  const [user, setUser] = useState({})
  const [questionnaireData, setQuestionnaireData] = useState({})
  const [lockedSignupInfo, setLockedSignupInfo] = useState([])
  const [tokenStatusData, setTokenStatusData] = useState(null)
  const [tokenValidation, setTokenValidation] = useState({
    isChecking: true,
    isValid: false,
    error: null,
  })
  
  const params = match.params
  const queryParams = queryString.parse(location.search)
  
  // Extract token from query params
  const passwordToken = queryParams.token
  
  // Validate token on mount
  useEffect(() => {
    const validateToken = async () => {
      // Check if token exists
      if (!passwordToken) {
        setTokenValidation({
          isChecking: false,
          isValid: false,
          error: "Access denied. Sign up requires a valid invitation token.",
        })
        return
      }

      // Validate token with backend
      try {
        const result = await services.employeeServices.checkToken(passwordToken)
        setTokenStatusData(result?.data || null)

        // If token_status returns membership application context, derive and lock the sign-up choices
        const membershipApplication = result?.data?.membership_application || null
        const businessType = membershipApplication?.business_type
        const businessCategory = membershipApplication?.business_category

        // Derive role/type from application data (preferred) or from account_type (fallback)
        // Business rule:
        // - If businessCategory === 'professional' => Professional sign up flow (internal role: 'individual')
        // - Else => Company/Organization sign up flow (internal role: 'company')
        const accountType = result?.data?.user?.account_type
        const isProfessionalFromCategory = businessCategory === "professional"
        const isCompanyFromApplication =
          !isProfessionalFromCategory &&
          (businessType === "company_provider" || businessType === "company_seeker")
        const isCompanyFromAccountType =
          accountType === "company_admin" ||
          accountType === "associate_company_admin" ||
          accountType === "company_employee"

        const derivedRole =
          isProfessionalFromCategory
            ? "individual"
            : isCompanyFromApplication || isCompanyFromAccountType
              ? "company"
              : "individual"
        const derivedCompanyType = isCompanyFromApplication ? businessType : undefined

        setUser((prev) => ({
          ...prev,
          role: derivedRole,
          ...(derivedCompanyType ? { type: derivedCompanyType } : {}),
        }))

        // Pre-fill questionnaire fields for individual/professional with Membership Application data when available
        if (membershipApplication) {
          setQuestionnaireData((prev) => ({
            ...prev,
            firstName: prev.firstName || membershipApplication.first_name || "",
            lastName: prev.lastName || membershipApplication.last_name || "",
            email: prev.email || membershipApplication.email || "",
            companyName: prev.companyName || membershipApplication.company_name || "",
          }))
        }

        // Build read-only summary for the sign-up flow (values collected & approved in Membership Application)
        const bestDescribesLabel = businessCategory
          ? bestDescribesOptions.find((o) => o.id === businessCategory)?.value
          : null
        const providerSeekerLabel = businessType
          ? whichBusinessTypeOptions.find((o) => o.id === businessType)?.value
          : null

        const accountLabel = isProfessionalFromCategory
          ? "Professional"
          : "Company/Organization"
        const summary = [
          { label: "Account type", value: accountLabel },
          ...(providerSeekerLabel ? [{ label: "Provider/Seeker", value: providerSeekerLabel }] : []),
          ...(bestDescribesLabel ? [{ label: "Best describes", value: bestDescribesLabel }] : []),
        ]
        setLockedSignupInfo(summary)

        setTokenValidation({
          isChecking: false,
          isValid: true,
          error: null,
        })
      } catch (error) {
        setTokenValidation({  
          isChecking: false,
          isValid: false,
          error: "Invalid or expired token. Please contact support.",
        })
      }
    }

    validateToken()
  }, [passwordToken])

  // Check if this is a password setup flow (from subscription email)
  const isPasswordSetup = queryParams.setup === "true" && queryParams.token
  
  // Helper function to build sign up URL with query parameters preserved
  const buildSignUpUrl = (type, step) => {
    let url = paths.signUp(type, step)
    if (isPasswordSetup && passwordToken) {
      const separator = url.includes('?') ? '&' : '?'
      url += `${separator}token=${encodeURIComponent(passwordToken)}&setup=true`
    }
    return url
  }
  
  // Normalize step parameter: convert "step-1" to "1", "step-2" to "2", etc.
  // This allows backward compatibility with old URLs and new URLs
  const normalizedStep = params.step?.replace(/^step-/, "") || params.step

  // Skip redundant steps when Membership Application has already provided role/type
  useEffect(() => {
    if (!tokenValidation.isValid) return

    // Step 1 is redundant if role is already known
    if ((normalizedStep === "1" || normalizedStep === 1) && user.role) {
      if (user.role === "individual") {
        history.replace(buildSignUpUrl("/professional", "step-2"))
        return
      }
      if (user.role === "company") {
        // Step 2 is redundant for company if provider/seeker is already known
        if (user.type) {
          history.replace(buildSignUpUrl("/company", "step-3"))
        } else {
          history.replace(buildSignUpUrl("/company", "step-2"))
        }
      }
      return
    }

    // Company Step 2 is redundant if provider/seeker is already known
    if ((normalizedStep === "2" || normalizedStep === 2) && params.type === "company" && user.type) {
      history.replace(buildSignUpUrl("/company", "step-3"))
    }
  }, [tokenValidation.isValid, normalizedStep, params.type, user.role, user.type])
  
  // Normalize type parameter: convert "professional" to "individual" for internal logic
  // (backend still uses "individual" but URL shows "professional")
  const normalizedType = params.type === "professional" ? "individual" : params.type

  const onHandleNext = () => {
    if (user.role === "individual") {
      history.push(buildSignUpUrl("/professional", "step-2"))
    } else if (normalizedStep === "1" || normalizedStep === 1) {
      history.push(buildSignUpUrl("/company", "step-2"))
    } else if (user.role === "company" && user.type) {
      history.push(buildSignUpUrl("/company", "step-3"))
    } else if (user.role === "company") {
      history.push(buildSignUpUrl("/company", "step-2"))
    }
  }
  const onHandleNext2 = () => {
    document.querySelector("button[type='submit']").click()
  }

  const onHandleBack = () => {
    history.push(buildSignUpUrl("", "step-1"))
  }

  const onHandleBackStep2 = () => {
    history.push(buildSignUpUrl("/professional", "step-2"))
  }

  const onHandleBackStep3 = () => {
    if (params.type === "company" || params.type === "professional") {
      if (params.type === "company") {
        history.push(buildSignUpUrl("/company", "step-2"))
      } else {
        history.push(buildSignUpUrl("/professional", "step-2"))
      }
    } else {
      history.push(buildSignUpUrl("/professional", "step-2"))
    }
  }

  const onHandleRole = (role) => {
    setUser({ ...user, role })
  }

  const RedirectToStart = () => <Redirect to={buildSignUpUrl("", "step-1")} />
  
  // Block access if token validation fails
  if (tokenValidation.isChecking) {
    return (
      <div style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh",
        fontSize: "18px",
        color: "#666"
      }}>
        Verifying your invitation...
      </div>
    )
  }
  
  if (!tokenValidation.isValid) {
    return <Redirect to={paths.home} />
  }

  const handleAccountTypeChange = (type) => {
    setUser({ ...user, type })
  }

  const handleSaveQuestionnaire = async (values) => {
    // Store questionnaire data and move to step 3
    setQuestionnaireData(values)
    history.push(buildSignUpUrl("/professional", "step-3"))
    return Promise.resolve()
  }

  const handleSaveIndividualUser = async ({ fullName, password, email }) => {
    // If this is a password setup flow (from subscription email), use setPassword API
    if (isPasswordSetup && passwordToken) {
      try {
        const result = await services.employeeServices.setPassword(password, passwordToken, "reset_password")
        if (result && result.status === 200) {
          const accessToken = result?.data?.access_token
          if (accessToken) {
            // Existing confirmed users (e.g. password reset) continue to login
            history.push(paths.login)
          } else {
            // Newly onboarding users need to verify their email
            const emailForVerification = (email || questionnaireData?.email || queryParams.email || "").trim()
            if (emailForVerification) {
              history.push(paths.emailVerifyResend(emailForVerification))
            } else {
              history.push(paths.login)
            }
          }
        }
        return result
      } catch (error) {
        // Re-throw error so form can handle it
        throw error
      }
    }

    // Otherwise, proceed with normal sign up flow
    const params = {
      account_type: "individual",
      full_name: fullName,
      password,
      email,
      // Include questionnaire data if available
      first_name: questionnaireData.firstName,
      last_name: questionnaireData.lastName,
      company_name: questionnaireData.companyName,
      industry: questionnaireData.industry?.id,
      profession: questionnaireData.profession?.id,
    }

    try {
      const result = await services.userServices.signUp(params)
      // Only navigate on successful response
      if (result && result.status === 201) {
        history.push(paths.emailVerifyResend(params.email))
      }
      return result
    } catch (error) {
      // Re-throw error so form can handle it
      throw error
    }
  }
  const handleSaveCompanyUser = async (values) => {
    // If this is a password setup flow (from subscription email), use setPassword API
    if (isPasswordSetup && passwordToken) {
      try {
        const result = await services.employeeServices.setPassword(values.password, passwordToken, "reset_password")
        if (result && result.status === 200) {
          const accessToken = result?.data?.access_token
          if (accessToken) {
            history.push(paths.login)
          } else {
            const emailForVerification = (values.email || queryParams.email || "").trim()
            if (emailForVerification) {
              history.push(paths.emailVerifyResend(emailForVerification))
            } else {
              history.push(paths.login)
            }
          }
        }
        return result
      } catch (error) {
        // Re-throw error so form can handle it
        throw error
      }
    }

    // Otherwise, proceed with normal sign up flow
    const params = {
      account_type: "company_admin",
      full_name: values.fullName.trim(),
      email: values.email,
      password: values.password,
      company_type: user.type,
      company_name: values.companyName.trim(),
      team_size: values.teamSize.dataName,
    }

    try {
      await services.userServices
        .signUp(params)
        .then(() => history.push(paths.emailVerifyResend(params.email)))
    } catch (error) {
      throw error
    }
  }

  switch (normalizedStep) {
    case "1":
    case 1: {
      return (
        <SignUpStep1Container
          user={user}
          onHandleNext={onHandleNext}
          onHandleRole={onHandleRole}
        />
      )
    }
    case "2":
    case 2: {
      if (!user.role) {
        RedirectToStart()
      }
      if (params.type === "company") {
        return (
          <SignUpCompnayStep2Container
            user={user}
            onBack={onHandleBack}
            onAccountTypeChange={handleAccountTypeChange}
            onHandleNext={onHandleNext}
          />
        )
      } else {
        // Handle both "professional" and "individual" for backward compatibility
        return (
          <SignUpIndividualStep2Container
            handleSaveQuestionnaire={handleSaveQuestionnaire}
            onBack={onHandleBack}
            onHandleNext={onHandleNext2}
            questionnaireData={questionnaireData}
            lockedInfo={lockedSignupInfo}
          />
        )
      }
    }
    case "3":
    case 3: {
      // If this is a password setup flow, skip role checks and go directly to step 3
      // Handle both "professional" and "individual" for backward compatibility
      if (params.type === "company") {
        if (!isPasswordSetup && !user.type) {
          RedirectToStart()
        }
        return (
          <SignUpCompanyStep3Container
            handleSaveUser={handleSaveCompanyUser}
            onBack={isPasswordSetup ? undefined : onHandleBackStep3}
            onHandleNext={onHandleNext2}
            user={user}
            lockedInfo={lockedSignupInfo}
            initialValues={{
              fullName: tokenStatusData?.user?.full_name || "",
              email: tokenStatusData?.user?.email || "",
              companyName: tokenStatusData?.membership_application?.company_name || "",
            }}
          />
        )
      } else {
        // Individual step 3
        if (!isPasswordSetup && !user.role) {
          RedirectToStart()
        }
        return (
          <SignUpIndividualStep3Container
            handleSaveUser={handleSaveIndividualUser}
            onBack={isPasswordSetup ? undefined : onHandleBackStep2}
            onHandleNext={onHandleNext2}
            questionnaireData={questionnaireData}
            lockedInfo={lockedSignupInfo}
          />
        )
      }
    }
    default:
      RedirectToStart()
  }

  // if (params.step === "1") {
  //   return (
  //     <SignUpStep1Container
  //       user={user}
  //       onHandleNext={onHandleNext}
  //       onHandleRole={onHandleRole}
  //     />
  //   )
  // } else if (params.step === "2" && user.role) {
  //   if (params.type === "company") {
  //     return (
  //       <SignUpCompnayStep2Container
  //         user={user}
  //         onBack={onHandleBack}
  //         onAccountTypeChange={handleAccountTypeChange}
  //         onHandleNext={onHandleNext}
  //       />
  //     )
  //   } else {
  //     return (
  //       <SignUpIndividualStep2Container
  //         handleSaveUser={handleSaveIndividualUser}
  //         onBack={onHandleBack}
  //         onHandleNext={onHandleNext2}
  //       />
  //     )
  //   }
  // } else if (params.step === "3" && user.type) {
  //   return (
  //     <SignUpCompanyStep3Container
  //       handleSaveUser={handleSaveCompanyUser}
  //       onBack={onHandleBackStep3}
  //       onHandleNext={onHandleNext2}
  //       user={user}
  //     />
  //   )
  // } else {
  //   return <Redirect to={paths.signUp("", "1")} />
  // }
}

export default withRouter(SignUpPage)
